import * as ko from "knockout";
import * as Constants from "../common/constants";
import * as EventUtil from "../common/eventUtil";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as TargetTemplateData from "../data/targetTemplateData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

interface IFormItemObservable {
    ID: number;
    FormText: KnockoutObservable<string>;
    IsRequired: KnockoutObservable<boolean>;
    IsFormTextMissing: KnockoutObservable<boolean>;
    IsControlTypeMissing: KnockoutObservable<boolean>;
    ControlTypeID: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;
    ShowInGrid: KnockoutObservable<boolean>;
    ShowInMobileAppointmentHeader: KnockoutObservable<boolean>;
    CanChangeControlType: boolean;
    ListValues: KnockoutObservableArray<IListValueObservable>;
    TemplatePhraseID: KnockoutObservable<number>;
    PhraseOrCustomChoice: KnockoutObservable<string>;
    PhraseOrCustomOptions: KnockoutObservableArray<any>;
    Fields: KnockoutObservableArray<IFieldObservable>;
    NewFieldText: KnockoutObservable<string>;
    IsFieldsMissing: KnockoutObservable<boolean>;

}
interface IListValueObservable {
    ID: number;
    Value: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
}
interface IFieldObservable {
    ID: KnockoutObservable<number>;
    FieldText: KnockoutObservable<string>;
    FieldValue: KnockoutObservable<string>;
    SortOrder: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;
    HasException: KnockoutObservable<boolean>;
    HasExceptionForm: KnockoutObservable<boolean>;
    TemplateFormID: KnockoutObservable<number | null>;
}

interface IViewModel {
    TargetTemplate: {
        ID: KnockoutObservable<number>;
        TemplateName: KnockoutObservable<string>;
        TargetTemplateTypeID: KnockoutObservable<number>;
        AddressFormItems: KnockoutObservableArray<IFormItemObservable>;
        IsTemplateNameMissing: KnockoutObservable<boolean>;
        IsTargetTemplateTypeIDMissing: KnockoutObservable<boolean>;
    };
    ActiveFormItems: KnockoutObservableArray<IFormItemObservable>;
    InactiveFormItems: KnockoutObservableArray<IFormItemObservable>;
    ShowCustomFields: KnockoutObservable<boolean>;
    TargetTemplateTypes: KnockoutObservableArray<TargetTemplateData.ITargetTemplateType>;
    IsActive: KnockoutObservable<boolean>;
    QuestionLabel: string;
    ControlTypes: KnockoutObservableArray<TargetTemplateData.ITargetTemplateControlType>;
    toggleIsActive: () => void;
    getSelectTargetTemplateType: () => TargetTemplateData.ITargetTemplateType | null | undefined;
    showCustomFields?: KnockoutComputed<boolean>;
    showAddressFields?: KnockoutComputed<boolean>;
    TemplatePhrases: KnockoutObservableArray<TargetTemplateData.ITemplatePhrases>;
    onAddField: () => void;
    addListValue: (formItem: IFormItemObservable) => void;
    removeListValue: (formItem: IListValueObservable) => void;
    displayTypeIsRadio: (index: number) => boolean;
    displayTypeIsDropdown: (index: number) => boolean;
    onToggleFormItemIsActive: (formItem: IFormItemObservable) => void;
    onDeleteFormItem: (formItem: IFormItemObservable) => void;
    onMoveFormItemUp: (formItem: IFormItemObservable) => void;
    onMoveFormItemDown: (formItem: IFormItemObservable) => void;
    onDuplicateFormItem: (formItem: IFormItemObservable) => void;
    onInsertFormItem: (formItem: IFormItemObservable) => void;
    formTextForTitle: (index: number) => string;
    canMoveUp: (index: number) => boolean;
    canMoveDown: (index: number) => boolean;
    canDelete: (index: number) => boolean;
    displayShowInGrid: (index: number) => boolean;
    displayShowInMobileAppointmentHeader: (index: number) => boolean;
    canChangeTargetTemplateTypes: () => boolean;
    hasInactiveFormItems: () => boolean;
}

export class TargetTemplateController implements IController {
    private container: JQuery;
    private viewModel: IViewModel;
    private fieldsOnLoad: any = {};

    constructor() {
        const me: TargetTemplateController = this;

        this.viewModel = {
            TargetTemplate: {
                ID: ko.observable<number>(0),
                TemplateName: ko.observable<string>(),
                TargetTemplateTypeID: ko.observable<number>(),
                AddressFormItems: ko.observableArray<IFormItemObservable>(),
                IsTemplateNameMissing: ko.observable<boolean>(false),
                IsTargetTemplateTypeIDMissing: ko.observable<boolean>(false),
            },
            ActiveFormItems: ko.observableArray<IFormItemObservable>(),
            InactiveFormItems: ko.observableArray<IFormItemObservable>(),
            ShowCustomFields: ko.observable<boolean>(false),
            TargetTemplateTypes: ko.observableArray<TargetTemplateData.ITargetTemplateType>(),
            ControlTypes: ko.observableArray<TargetTemplateData.ITargetTemplateControlType>(),
            IsActive: ko.observable<boolean>(true),
            QuestionLabel: Constants.LABEL_QUESTION,
            TemplatePhrases: ko.observableArray<TargetTemplateData.ITemplatePhrases>(),

            toggleIsActive(): void {
                let message: string;

                if (this.IsActive()) {
                    message = "Are you sure you want to make this template inactive?";
                } else {
                    message = "Are you sure you want to make this template active?";
                }
                UI.showConfirmDialog(
                    message,
                    () => {
                        me.toggleIsActive();
                    });
            },

            // returns the TargetTemplateType object for the currently selected value
            getSelectTargetTemplateType(): TargetTemplateData.ITargetTemplateType | null | undefined {
                const targetTemplateTypeID: any = this.TargetTemplate.TargetTemplateTypeID();
                let match: TargetTemplateData.ITargetTemplateType | null | undefined;

                if (targetTemplateTypeID != null && targetTemplateTypeID > 0) {
                    const matches = this.TargetTemplateTypes().filter((targetTemplateType: TargetTemplateData.ITargetTemplateType): boolean => {
                            return targetTemplateType.ID === targetTemplateTypeID;
                        });
                    if (matches.length > 0) {
                        match = matches[0];
                    }
                }
                return match;
            },

            onAddField(): void {
                me.viewModel.ActiveFormItems.push(me.createEmptyFormItemObservable());
            },
            onToggleFormItemIsActive(formItem: IFormItemObservable): void {
                if (formItem.IsActive()) {
                    UI.showConfirmDialog(
                    "Are you sure you want to make this item inactive?  It will not be displayed on any forms or reports.  This action can be undone.",
                    () => {
                        me.viewModel.ActiveFormItems.remove(formItem);
                        me.viewModel.InactiveFormItems.push(formItem);
                        formItem.IsActive(false);
                    });
                } else {
                    me.viewModel.ActiveFormItems.push(formItem);
                    me.viewModel.InactiveFormItems.remove(formItem);
                    formItem.IsActive(true);
                }
            },
            onDeleteFormItem(formItem: IFormItemObservable): void {
                me.viewModel.ActiveFormItems.remove(formItem);
            },
            onMoveFormItemUp(formItem: IFormItemObservable): void {
                const index = me.viewModel.ActiveFormItems.indexOf(formItem);
                me.viewModel.ActiveFormItems.remove(formItem);
                me.viewModel.ActiveFormItems.splice(index - 1, 0, formItem);
            },
            onMoveFormItemDown(formItem: IFormItemObservable): void {
                const index = me.viewModel.ActiveFormItems.indexOf(formItem);
                me.viewModel.ActiveFormItems.remove(formItem);
                me.viewModel.ActiveFormItems.splice(index + 1, 0, formItem);
            },
            onDuplicateFormItem(formItem: IFormItemObservable): void {
                const index = me.viewModel.ActiveFormItems.indexOf(formItem);
                me.viewModel.ActiveFormItems.splice(index + 1, 0, me.copyFormItemObservable(formItem));
            },
            onInsertFormItem(formItem: IFormItemObservable): void {
                const index = me.viewModel.ActiveFormItems.indexOf(formItem);
                me.viewModel.ActiveFormItems.splice(index + 1, 0, me.createEmptyFormItemObservable());
            },
            hasInactiveFormItems(): boolean {
                return me.viewModel.InactiveFormItems().length > 0;
            },
            formTextForTitle(index: number): string {
                const formItem = me.viewModel.ActiveFormItems()[index];
                if (formItem.FormText() !== null && formItem.FormText().length > 0) {
                    return formItem.FormText();
                } else if (formItem.ID > 0) {
                    return `(No ${Constants.LABEL_QUESTION} Title)`;
                } else {
                    return `(New ${Constants.LABEL_QUESTION})`;
                }
            },
            canMoveUp(index: number): boolean {
                return index > 1;
            },
            canMoveDown(index: number): boolean {
                return index > 0 && index + 1 < me.viewModel.ActiveFormItems().length;
            },
            canDelete(index: number): boolean {
                return me.viewModel.ActiveFormItems()[index].ID <= 0;
            },
            displayShowInGrid(index: number): boolean {
                const controlType = me.getControlTypeForFormItemIndex(index);
                return controlType != null && !controlType.IsBinaryData;
            },
            displayShowInMobileAppointmentHeader(index: number): boolean {
                const controlType = me.getControlTypeForFormItemIndex(index);
                return controlType != null && !controlType.IsBinaryData;
            },
            canChangeTargetTemplateTypes(): boolean {
                return me.viewModel.TargetTemplate.ID() <= 0;
            },
            addListValue(formItem: IFormItemObservable): void {
                if (formItem.NewFieldText() !== "") {
                    formItem.ListValues.push({
                        ID: 0,
                        Value: ko.observable(formItem.NewFieldText()),
                        IsActive: ko.observable(true),
                    });
                    formItem.NewFieldText("");
                }
            },
            removeListValue(formItem: IListValueObservable): void {
                formItem.IsActive(false);
            },
            displayTypeIsRadio(index: number): boolean {
                const controlType = me.getControlTypeForFormItemIndex(index);
                return controlType != null && controlType.ID === Constants.QuestionType.RADIOBUTTONLIST;
            },
            displayTypeIsDropdown(index: number): boolean {
                const controlType = me.getControlTypeForFormItemIndex(index);
                return controlType != null && controlType.ID === Constants.QuestionType.DROPDOWN;
            },
        };

        // determine if the custom fields should be shown based on the selected type
        this.viewModel.showCustomFields = ko.pureComputed((): boolean => {
            const targetTemplateType: TargetTemplateData.ITargetTemplateType|null|undefined = me.viewModel.getSelectTargetTemplateType();
            if (targetTemplateType != null) { return targetTemplateType.HasCustomFields; }
            return false;
        });

        // determine if the address fields should be shown based on the selected type
        this.viewModel.showAddressFields = ko.pureComputed((): boolean => {
            const targetTemplateType: TargetTemplateData.ITargetTemplateType|null|undefined = me.viewModel.getSelectTargetTemplateType();
            if (targetTemplateType != null) { return targetTemplateType.HasAddressFields; }
            return false;
        });

    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;
        ko.applyBindings(this.viewModel, container[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        let id: number = 0;
        if (params != null && params.length === 1) {
            id = params[0];
        }
        return this.loadRecord(id);
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = JSON.stringify(this.GetTargetTemplateFromViewModel());
        return fieldValues !== this.fieldsOnLoad;
    }

    public appRouterID(params: any[]): string {
        if (params != null && params.length === 1) {
            return "TargetTemplate" + params[0];
        } else {
            return "TargetTemplateNew";
        }
    }

    // returns a title for the page
    public title(): string {
        return "Target Template";
    }

    public subTitle(): string {
        if (this.viewModel.TargetTemplate.ID() > 0) {
            return this.viewModel.TargetTemplate.TemplateName();
        } else {
            return "New";
        }
    }
    public save(): JQueryPromise<any> {
        const me: TargetTemplateController = this;
        const app: IApplication = Global.app();
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        let waitContainer: JQuery;

        if (!this.isValid()) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            deferred.reject();
            return deferred.promise();
        }

        waitContainer = UI.showWaitIndicator(this.container);
        TargetTemplateData.Save(this.GetTargetTemplateFromViewModel())
            .done((response: Data.IResponse<TargetTemplateData.ITargetTemplate>) => {
                let currentID: number;
                if (response.Success) {
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);

                    // let other pages know the template changes
                    app.events().trigger(EventUtil.EVENT_TARGETTEMPLATECHANGE, { targetTemplateID: response.Payload.ID });

                    currentID = me.viewModel.TargetTemplate.ID();
                    me.updateTargetTemplateViewModel(response.Payload);
                    if (currentID <= 0) {
                        // add a new record, so update the URL
                        me.updateURL(response.Payload.ID, response.Payload.TemplateName);
                    } else {
                        // updating an existing record
                        app.pageManager().refreshCurrentPageTitle();
                    }
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    private GetTargetTemplateFromViewModel(): TargetTemplateData.ITargetTemplate {
        const targetTemplateVM = this.viewModel.TargetTemplate;
        const targetTemplate: TargetTemplateData.ITargetTemplate = {
            ID: targetTemplateVM.ID(),
            TemplateName: targetTemplateVM.TemplateName(),
            TargetTemplateTypeID: targetTemplateVM.TargetTemplateTypeID(),
            IsActive: this.viewModel.IsActive(),
            FormItems: this.GetTargetTemplateFormItemsFromObservable(this.viewModel.ActiveFormItems),
            AddressFormItems: this.GetTargetTemplateFormItemsFromObservable(targetTemplateVM.AddressFormItems),

        };
        if (this.viewModel.InactiveFormItems().length > 0) {
            targetTemplate.FormItems = targetTemplate.FormItems.concat(this.GetTargetTemplateFormItemsFromObservable(this.viewModel.InactiveFormItems));
        }
        return targetTemplate;
    }

    private GetTargetTemplateFormItemsFromObservable(formItemObservables: KnockoutObservableArray<IFormItemObservable>): TargetTemplateData.ITargetTemplateFormItem[] {
        const formItems: TargetTemplateData.ITargetTemplateFormItem[] = [];
        for (const formItemObservable of formItemObservables()) {
            const listValues: TargetTemplateData.IListValue[] = [];
            for (const listValueObservables of formItemObservable.ListValues()) {
                listValues.push({
                    ID: listValueObservables.ID,
                    Value: listValueObservables.Value(),
                    IsActive: listValueObservables.IsActive(),
                });
            }

            formItems.push({
                ID: formItemObservable.ID,
                FormText: formItemObservable.FormText(),
                IsRequired: formItemObservable.IsRequired(),
                ControlTypeID: formItemObservable.ControlTypeID(),
                IsActive: formItemObservable.IsActive(),
                ShowInGrid: formItemObservable.ShowInGrid(),
                ShowInMobileAppointmentHeader: formItemObservable.ShowInMobileAppointmentHeader(),
                CanChangeControlType: formItemObservable.CanChangeControlType,
                ListValues: listValues,
                TemplatePhraseID: (formItemObservable.PhraseOrCustomChoice() === Constants.TEMPLATE_FORM_CHOICES_PHRASES ? formItemObservable.TemplatePhraseID() : 0),

            });
        }
        return formItems;
    }

    private loadRecord(id: number): JQueryPromise<any> {
        const me: TargetTemplateController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        TargetTemplateData.GetMaster(id)
            .done((response: Data.IResponse<TargetTemplateData.ITargetTemplateMaster>) => {
                if (response.Success) {
                    me.updateViewModel(response.Payload);

                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private isValid(): boolean {
        let isValid: boolean = true;
        const targetTemplate = this.viewModel.TargetTemplate;
        const targetTemplateType: TargetTemplateData.ITargetTemplateType | null | undefined = this.viewModel.getSelectTargetTemplateType();

        isValid = UI.setRequiredString(targetTemplate.TemplateName, targetTemplate.IsTemplateNameMissing) ? isValid : false;
        isValid = UI.setRequiredNumber(targetTemplate.TargetTemplateTypeID, targetTemplate.IsTargetTemplateTypeIDMissing, true) ?
            isValid : false;

        if (targetTemplateType != null && targetTemplateType.HasCustomFields) {
            for (const formItem of this.viewModel.ActiveFormItems()) {
                isValid = UI.setRequiredNumber(formItem.ControlTypeID, formItem.IsControlTypeMissing, true) ? isValid : false;
                isValid = UI.setRequiredString(formItem.FormText, formItem.IsFormTextMissing) ? isValid : false;

                formItem.IsFieldsMissing(false);
                if (( formItem.TemplatePhraseID() === null) && this.numberOfActiveListValues(formItem.ListValues) === 0 &&
                    (formItem.ControlTypeID() === Constants.QuestionType.DROPDOWN ||
                    formItem.ControlTypeID() === Constants.QuestionType.RADIOBUTTONLIST)) {
                        formItem.IsFieldsMissing(true);
                        isValid = false;

                }
            }
        }

        return isValid;

    }
    private numberOfActiveListValues(values: KnockoutObservableArray<IListValueObservable>): number {
        let count: number = 0;

        values().forEach((value: IListValueObservable) => {
            if (value.IsActive()) {
                count++;
            }
        });
        return count;
    }

    // used to update the URL when a new record is added
    private updateURL(newID: number, templateName: string): void {
        const app: IApplication = Global.app();
        const routeChange: EventUtil.IRouteChange = {
            appRouterID: this.appRouterID([]),
            url: "/TargetTemplate/" + newID,
            title: this.title(),
            subTitle: templateName,
            newAppRouterID: this.appRouterID([newID]),
        };

        app.events().trigger(EventUtil.EVENT_ROUTECHANGE, routeChange);
    }

    private updateViewModel(master: TargetTemplateData.ITargetTemplateMaster): void {
        // select controls must be updated before updating the rest of the view model
        this.viewModel.TargetTemplateTypes(master.TargetTemplateTypes);
        this.viewModel.TargetTemplateTypes.splice(0, 0, { ID: 0, Name: "(select template type)", HasCustomFields: false, HasAddressFields: false });

        this.viewModel.ControlTypes(master.ControlTypes);
        this.viewModel.ControlTypes.splice(0, 0, { ID: 0, Name: "(select a control type)", IsBinaryData: false, HasListValues: false});

        this.viewModel.TemplatePhrases(master.TemplatePhrases);

        this.updateTargetTemplateViewModel(master.TargetTemplate);
    }

    private updateTargetTemplateViewModel(targetTemplate: TargetTemplateData.ITargetTemplate): void {
        const me: TargetTemplateController = this;

        this.viewModel.TargetTemplate.ID(targetTemplate.ID);
        this.viewModel.TargetTemplate.TemplateName(targetTemplate.TemplateName);
        this.viewModel.TargetTemplate.TargetTemplateTypeID(targetTemplate.TargetTemplateTypeID);

        this.viewModel.ActiveFormItems.removeAll();
        this.viewModel.InactiveFormItems.removeAll();
        for (const formItem of targetTemplate.FormItems) {
            if (formItem.IsActive) {
                me.viewModel.ActiveFormItems.push(me.createFormItemObservable(formItem));
            } else {
                me.viewModel.InactiveFormItems.push(me.createFormItemObservable(formItem));
            }
        }

        if (targetTemplate.FormItems.length === 0 ) {
            // New target, add the key field by default
            this.viewModel.ActiveFormItems.push(this.createEmptyFormItemObservable());
        }
        this.viewModel.TargetTemplate.AddressFormItems.removeAll();
        for (const formItem of targetTemplate.AddressFormItems) {
            me.viewModel.TargetTemplate.AddressFormItems.push(me.createFormItemObservable(formItem));
        }

        this.viewModel.IsActive(targetTemplate.IsActive);

        // remember the fields value so we can determine if they have changed
        this.fieldsOnLoad = JSON.stringify(this.GetTargetTemplateFromViewModel());
        me.viewModel.TemplatePhrases.unshift(me.GetCodeModelObservable({ ID: null, Name: Constants.PHRASE_NOTSELECTED }));

    }

    private createFormItemObservable(formItem: TargetTemplateData.ITargetTemplateFormItem): IFormItemObservable {
        const  listValueObservables: IListValueObservable[] = [];
        for (const listValue of formItem.ListValues) {
            listValueObservables.push({
                ID: listValue.ID,
                Value: ko.observable(listValue.Value),
                IsActive: ko.observable(listValue.IsActive),
            });
        }
        const observable: IFormItemObservable = {
            ID: formItem.ID,
            FormText: ko.observable<string>(formItem.FormText),
            IsRequired: ko.observable<boolean>(formItem.IsRequired),
            IsFormTextMissing: ko.observable<boolean>(false),
            IsControlTypeMissing: ko.observable<boolean>(false),
            ControlTypeID: ko.observable(formItem.ControlTypeID),
            IsActive: ko.observable(formItem.IsActive),
            ShowInGrid: ko.observable(formItem.ShowInGrid),
            ShowInMobileAppointmentHeader: ko.observable(formItem.ShowInMobileAppointmentHeader),
            CanChangeControlType: formItem.CanChangeControlType,
            ListValues: ko.observableArray(listValueObservables),
            TemplatePhraseID: ko.observable(formItem ? formItem.TemplatePhraseID : 0),
            PhraseOrCustomChoice: ko.observable(formItem.TemplatePhraseID == null ? Constants.TEMPLATE_FORM_CHOICES_CUSTOM : Constants.TEMPLATE_FORM_CHOICES_PHRASES),
            PhraseOrCustomOptions: ko.observableArray([{ text: "Enter Values", value: Constants.TEMPLATE_FORM_CHOICES_CUSTOM },
                   { text: "Use Template Phrases", value: Constants.TEMPLATE_FORM_CHOICES_PHRASES}]),
            Fields: ko.observableArray<IFieldObservable>(),
            NewFieldText: ko.observable(""),
            IsFieldsMissing: ko.observable<boolean>(false),
        };
        return observable;
    }

    private createEmptyFormItemObservable(): IFormItemObservable {
        const observable: IFormItemObservable = {
            ID: 0,
            FormText: ko.observable<string>(""),
            IsRequired: ko.observable<boolean>(false),
            IsFormTextMissing: ko.observable<boolean>(false),
            IsControlTypeMissing: ko.observable<boolean>(false),
            ControlTypeID: ko.observable(Constants.QuestionType.TEXTBOX),
            IsActive: ko.observable(true),
            ShowInGrid: ko.observable(true),
            ShowInMobileAppointmentHeader: ko.observable(true),
            CanChangeControlType: true,
            ListValues: ko.observableArray<IListValueObservable>(),
            TemplatePhraseID: ko.observable(0),
            PhraseOrCustomChoice: ko.observable(Constants.TEMPLATE_FORM_CHOICES_CUSTOM ),
            PhraseOrCustomOptions: ko.observableArray([{ text: "Enter Values", value: Constants.TEMPLATE_FORM_CHOICES_CUSTOM },
                    { text: "Use Template Phrases", value: Constants.TEMPLATE_FORM_CHOICES_PHRASES}]),

            Fields: ko.observableArray<IFieldObservable>(),
            NewFieldText: ko.observable(""),
            IsFieldsMissing: ko.observable(false),
        };
        observable.PhraseOrCustomChoice.subscribe((data: any) => {
            if (!data) { return; }
            // Clear out form fields but we don't have the template id yet
            if (data === Constants.TEMPLATE_FORM_CHOICES_CUSTOM) {
                // Clear the TemplatePhraseID
                observable.TemplatePhraseID(null);
            }

        });

        return observable;
    }

    private copyFormItemObservable(source: IFormItemObservable): IFormItemObservable {
        const observable: IFormItemObservable = {
            ID: 0,
            FormText: ko.observable<string>(source.FormText()),
            IsRequired: ko.observable<boolean>(source.IsRequired()),
            IsFormTextMissing: ko.observable<boolean>(source.IsFormTextMissing()),
            IsControlTypeMissing: ko.observable<boolean>(source.IsControlTypeMissing()),
            ControlTypeID: ko.observable(source.ControlTypeID()),
            IsActive: ko.observable(source.IsActive()),
            ShowInGrid: ko.observable(source.ShowInGrid()),
            ShowInMobileAppointmentHeader: ko.observable(source.ShowInMobileAppointmentHeader()),
            CanChangeControlType: true,
            ListValues: ko.observableArray(source.ListValues()),
            TemplatePhraseID: ko.observable(source.TemplatePhraseID()),
            PhraseOrCustomChoice: ko.observable(source.PhraseOrCustomChoice()),
            PhraseOrCustomOptions: ko.observableArray(source.PhraseOrCustomOptions()),
            Fields: ko.observableArray<IFieldObservable>(source.Fields()),
            NewFieldText: ko.observable(source.NewFieldText()),
            IsFieldsMissing: ko.observable(source.IsFieldsMissing()),
        };
        return observable;
    }

    private toggleIsActive(): JQueryPromise<any> {
        const me: TargetTemplateController = this;
        const app: IApplication = Global.app();
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);
        const newIsActive: boolean = !this.viewModel.IsActive();
        const targetTemplateID: number = this.viewModel.TargetTemplate.ID();
        const request: TargetTemplateData.ITargetTemplateSetIsActiveRequest = {
            TargetTemplateID: targetTemplateID,
            IsActive: newIsActive,
        };

        TargetTemplateData.SetIsActive(request)
            .done((response: Data.IResponse<TargetTemplateData.ITargetTemplate>) => {
                if (response.Success) {
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);

                    // let other pages know the template changes
                    app.events().trigger(EventUtil.EVENT_TARGETTEMPLATECHANGE, { targetTemplateID });

                    me.viewModel.IsActive(newIsActive);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve(true);
            });

        return deferred.promise();
    }

    private getControlTypeForFormItemIndex(formItemIndex: number): TargetTemplateData.ITargetTemplateControlType | null {
        const controlTypeID = this.viewModel.ActiveFormItems()[formItemIndex].ControlTypeID();
        const controlType = this.viewModel.ControlTypes().filter((x) => x.ID === controlTypeID);
        if (controlType.length > 0) {
            return controlType[0];
        } else {
            return null;
        }
    }
    private GetCodeModelObservable(src: Data.ICodeModel<number>): any {
        return {
            ID: ko.observable(src ? src.ID : 0),
            Name: ko.observable(src ? src.Name : ""),
        };
    }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/targetTemplate.ts